import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellDate,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

import { type CommonAssortmentTableItem } from './common-columns.types';

const COLUMN_ID = 'first_sale_date' as const;

export type FirstSaleDateColumnId = typeof COLUMN_ID;

interface FirstSaleDateFields {
  first_sale_date: CommonAssortmentTableItem['first_sale_date'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<FirstSaleDateFields>();

export const createFirstSaleDateColumn = <
  T extends FirstSaleDateFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.first_sale_date),
    {
      id: COLUMN_ID,
      size: 175,
      header: function FirstSaleDateHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const headerCellContents = (
          <DataTableCellContent align={'right'}>
            <DataTableCellTitle className="uppercase font-medium whitespace-normal text-end">
              {t('table-columns.first-sale-date')}
            </DataTableCellTitle>
          </DataTableCellContent>
        );

        if (
          tableName === 'productsTable' ||
          tableName === 'productLocationsTable'
        ) {
          return (
            <DataTableHeaderCellWithSorting
              column={column}
              arrowPosition="left"
              showSortable
            >
              {headerCellContents}
            </DataTableHeaderCellWithSorting>
          );
        }

        return headerCellContents;
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function FirstSaleDateCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableCellDate value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
